import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { FileText, CheckCircle2, Eye, X, HeartPulse, Check, Download } from "lucide-react";
import { toast } from "sonner";
import MedicalFormModal from "./MedicalFormModal";
import KrakenLoader from "@/components/KrakenLoader";

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); } catch { return iso; }
}

// Render body text with basic bold (**text**) and preserve newlines
function FormattedBody({ text }) {
  if (!text) return null;
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return (
    <div className="text-slate-200 text-sm leading-relaxed whitespace-pre-wrap">
      {parts.map((p, i) =>
        /^\*\*[^*]+\*\*$/.test(p)
          ? <strong key={i} className="text-white">{p.slice(2, -2)}</strong>
          : <span key={i}>{p}</span>
      )}
    </div>
  );
}

export default function Documents() {
  const { user } = useAuth();
  const [docs, setDocs] = useState([]);
  const [sigs, setSigs] = useState([]);
  const [signingDoc, setSigningDoc] = useState(null);
  const [viewingSig, setViewingSig] = useState(null);
  const [form, setForm] = useState({ signed_name: "", co_signed_name: "", agreed: false, photo_video_consent: "" });
  const [submitting, setSubmitting] = useState(false);
  const [medicalOpen, setMedicalOpen] = useState(false);
  const [medical, setMedical] = useState(null);

  const [loading, setLoading] = useState(true);
  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/documents").then((r) => setDocs(r.data)),
      api.get("/signatures/mine").then((r) => setSigs(r.data)),
      api.get("/medical-form/mine").then((r) => setMedical(r.data && Object.keys(r.data).length ? r.data : null)),
    ]).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const sigFor = (docId) => sigs.find((s) => s.document_id === docId);
  const isMedicalDoc = (d) => (d.category === "medical") || /medical information/i.test(d.title || "");
  const isPhotoVideoDoc = (d) => (d.category === "permission") && /photo.*video/i.test(d.title || "");

  const openSign = (doc) => {
    setForm({
      signed_name: user?.name || "",
      co_signed_name: doc.requires_co_signer ? (user?.player_name || "") : "",
      agreed: false,
      photo_video_consent: "",
    });
    setSigningDoc(doc);
  };

  const submitSignature = async (e) => {
    e.preventDefault();
    if (!form.agreed) { toast.error("Please confirm the checkbox to continue."); return; }
    if (!form.signed_name.trim()) { toast.error("Please type your full name."); return; }
    if (signingDoc.requires_co_signer && !form.co_signed_name.trim()) {
      toast.error("Please type the player's full name."); return;
    }
    const photoVideo = isPhotoVideoDoc(signingDoc);
    if (photoVideo && !["yes", "no"].includes(form.photo_video_consent)) {
      toast.error("Please select Yes or No for photo/video consent."); return;
    }
    setSubmitting(true);
    try {
      // For Player Promise: signed_name = player typed name, co_signed_name = parent typed name confirming review
      // For Parent Promise: signed_name = parent typed name
      const base = signingDoc.requires_co_signer
        ? { document_id: signingDoc.id, signed_name: form.co_signed_name, co_signed_name: form.signed_name, agreed: true }
        : { document_id: signingDoc.id, signed_name: form.signed_name, agreed: true };
      const payload = photoVideo ? { ...base, photo_video_consent: form.photo_video_consent } : base;
      await api.post("/signatures", payload);
      toast.success("Signed successfully.");
      setSigningDoc(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to sign");
    } finally {
      setSubmitting(false);
    }
  };

  const required = docs.filter((d) => d.requires_signature);
  const other = docs.filter((d) => !d.requires_signature);

  const renderCard = (d) => {
    const sig = sigFor(d.id);
    const medicalDoc = isMedicalDoc(d);
    const isSigned = medicalDoc ? !!medical : !!sig;
    return (
      <div key={d.id} className="kr-card rounded-xl p-5" data-testid={`doc-${d.id}`}>
        <div className="flex items-start justify-between gap-3 mb-3">
          {medicalDoc ? <HeartPulse className="text-[#00E5FF]" size={22} /> : <FileText className="text-[#00E5FF]" size={22} />}
          {d.requires_signature && (
            <span className={`text-[10px] uppercase tracking-widest border rounded-full px-2 py-1 inline-flex items-center gap-1.5 ${
              isSigned ? "bg-green-500/15 text-green-300 border-green-400/40 status-success" : "bg-red-500/15 text-red-300 border-red-400/40 status-missing"
            }`}>
              {isSigned && <Check size={10} className="check-pop" />}
              {isSigned ? (medicalDoc ? "Submitted" : "Signed") : (medicalDoc ? "Not Submitted" : "Not Signed")}
            </span>
          )}
        </div>
        <div className="font-display text-lg uppercase">{d.title}</div>
        <div className="text-xs uppercase tracking-widest text-slate-400 mt-1">
          {d.requires_signature ? "Required" : d.category.replace("_", " ")}
        </div>
        {d.description && <p className="text-slate-300 text-sm mt-2">{d.description}</p>}

        {d.file_url && (
          <a
            href={d.file_url}
            target="_blank"
            rel="noreferrer"
            className="mt-3 inline-flex items-center gap-1.5 text-xs text-[#00E5FF] hover:text-white bg-[#00E5FF]/10 border border-[#00E5FF]/30 rounded-full px-3 py-1.5 transition"
            data-testid={`doc-download-${d.id}`}
          >
            <Download size={12} /> {d.file_name || "Download file"}
          </a>
        )}

        {d.requires_signature && (
          medicalDoc ? (
            isSigned ? (
              <div className="mt-4 space-y-2">
                <div className="flex items-center gap-2 text-[#00E5FF] text-sm" data-testid={`signed-${d.id}`}>
                  <CheckCircle2 size={16} /> Submitted on {fmtDate(medical.updated_at || medical.created_at)}
                </div>
                <div className="flex flex-wrap gap-2">
                  <button onClick={() => setMedicalOpen(true)} className="btn-ghost text-xs" data-testid={`view-${d.id}`}>
                    <Eye size={12} /> View / Update
                  </button>
                </div>
              </div>
            ) : (
              <button onClick={() => setMedicalOpen(true)} className="btn-primary mt-4 text-sm" data-testid={`sign-${d.id}`}>
                Fill Out Medical Form
              </button>
            )
          ) : (
            isSigned ? (
              <div className="mt-4 space-y-2">
                <div className="flex items-center gap-2 text-[#00E5FF] text-sm" data-testid={`signed-${d.id}`}>
                  <CheckCircle2 size={16} /> Signed on {fmtDate(sig.signed_at)}
                </div>
                <button
                  onClick={() => setViewingSig({ ...sig, document: d })}
                  className="btn-ghost text-xs"
                  data-testid={`view-${d.id}`}
                >
                  <Eye size={12} /> View Signed Copy
                </button>
              </div>
            ) : (
              <button onClick={() => openSign(d)} className="btn-primary mt-4 text-sm" data-testid={`sign-${d.id}`}>
                Review & Sign
              </button>
            )
          )
        )}
      </div>
    );
  };

  return (
    <div data-testid="documents-page">
      <div className="eyebrow mb-1">Team Documents</div>
      <h1 className="font-display text-3xl uppercase mb-6">Library</h1>

      {loading && <KrakenLoader label="Loading documents…" testId="documents-loader" />}

      {!loading && required.length > 0 && (
        <div className="mb-8">
          <div className="eyebrow mb-3">Required Forms</div>
          <div className="grid sm:grid-cols-2 gap-4">{required.map(renderCard)}</div>
        </div>
      )}

      {!loading && other.length > 0 && (
        <div>
          <div className="eyebrow mb-3">Other Documents</div>
          <div className="grid sm:grid-cols-2 gap-4">{other.map(renderCard)}</div>
        </div>
      )}

      {/* SIGN MODAL */}
      {signingDoc && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 overflow-y-auto"
             onClick={() => !submitting && setSigningDoc(null)} data-testid="sign-modal">
          <div onClick={(e) => e.stopPropagation()} className="kr-card rounded-2xl p-6 sm:p-8 max-w-2xl w-full my-8 relative">
            <button onClick={() => setSigningDoc(null)} className="absolute top-4 right-4 text-slate-400 hover:text-white"><X size={20} /></button>
            <div className="eyebrow mb-1">Read & Sign</div>
            <h2 className="font-display text-2xl uppercase mb-3">{signingDoc.title}</h2>
            <div className="text-xs text-slate-400 mb-4">
              {signingDoc.requires_co_signer
                ? `Player: ${user?.player_name || "—"} · Parent: ${user?.name || "—"}`
                : `Parent/Guardian: ${user?.name || "—"}`}
            </div>

            {signingDoc.body && (
              <div className="bg-[#050A15] rounded-lg p-5 max-h-[40vh] overflow-y-auto border border-white/10 mb-5" data-testid="sign-body">
                <FormattedBody text={signingDoc.body} />
              </div>
            )}

            <form onSubmit={submitSignature} className="space-y-3">
              {signingDoc.requires_co_signer ? (
                <>
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-[#00E5FF] mb-1">Player Typed Name</label>
                    <input required value={form.co_signed_name} onChange={(e) => setForm({ ...form, co_signed_name: e.target.value })} className="w-full bg-[#050A15] border border-white/10 rounded-md p-3" data-testid="sign-player-name" />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-[#00E5FF] mb-1">Parent/Guardian Typed Name (confirming review)</label>
                    <input required value={form.signed_name} onChange={(e) => setForm({ ...form, signed_name: e.target.value })} className="w-full bg-[#050A15] border border-white/10 rounded-md p-3" data-testid="sign-parent-name" />
                  </div>
                </>
              ) : (
                <div>
                  <label className="block text-xs uppercase tracking-widest text-[#00E5FF] mb-1">Parent/Guardian Typed Name</label>
                  <input required value={form.signed_name} onChange={(e) => setForm({ ...form, signed_name: e.target.value })} className="w-full bg-[#050A15] border border-white/10 rounded-md p-3" data-testid="sign-parent-name" />
                </div>
              )}

              <div className="text-xs text-slate-400">Date signed: <span className="text-white">{new Date().toLocaleDateString()}</span></div>

              {isPhotoVideoDoc(signingDoc) && (
                <div className="bg-[#050A15] border border-white/10 rounded-lg p-4 mt-1" data-testid="photo-video-consent">
                  <div className="text-xs uppercase tracking-widest text-[#00E5FF] mb-2">Photo / Video Consent</div>
                  <p className="text-sm text-slate-300 mb-3">
                    Do you grant Coastal Kraken Baseball permission to use your player&apos;s photos and videos on team channels (social media, website, promotional material)?
                  </p>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <label className={`flex-1 cursor-pointer rounded-md border p-3 flex items-center gap-3 transition ${form.photo_video_consent === "yes" ? "border-[#00E5FF] bg-[#00E5FF]/10" : "border-white/10 hover:border-white/30"}`} data-testid="photo-video-yes-label">
                      <input
                        type="radio"
                        name="photo_video_consent"
                        value="yes"
                        checked={form.photo_video_consent === "yes"}
                        onChange={() => setForm({ ...form, photo_video_consent: "yes" })}
                        data-testid="photo-video-yes"
                        className="accent-[#00E5FF]"
                      />
                      <div>
                        <div className="text-white font-semibold text-sm">Yes, I grant permission</div>
                        <div className="text-xs text-slate-400">Photos/videos may be used by the team.</div>
                      </div>
                    </label>
                    <label className={`flex-1 cursor-pointer rounded-md border p-3 flex items-center gap-3 transition ${form.photo_video_consent === "no" ? "border-red-400 bg-red-400/10" : "border-white/10 hover:border-white/30"}`} data-testid="photo-video-no-label">
                      <input
                        type="radio"
                        name="photo_video_consent"
                        value="no"
                        checked={form.photo_video_consent === "no"}
                        onChange={() => setForm({ ...form, photo_video_consent: "no" })}
                        data-testid="photo-video-no"
                        className="accent-red-400"
                      />
                      <div>
                        <div className="text-white font-semibold text-sm">No, do not use</div>
                        <div className="text-xs text-slate-400">Please withhold photos/videos.</div>
                      </div>
                    </label>
                  </div>
                </div>
              )}

              <label className="flex items-start gap-3 text-sm text-slate-300 cursor-pointer pt-2">
                <input required type="checkbox" checked={form.agreed} onChange={(e) => setForm({ ...form, agreed: e.target.checked })} data-testid="sign-agree" className="mt-1" />
                <span>I agree and understand this {signingDoc.title.replace("Coastal Kraken Baseball ", "")}.</span>
              </label>

              <div className="flex gap-2 pt-2">
                <button type="button" onClick={() => setSigningDoc(null)} className="btn-ghost flex-1 justify-center">Cancel</button>
                <button type="submit" disabled={submitting} className="btn-primary flex-1 justify-center" data-testid="sign-submit">
                  {submitting ? "Signing…" : "Sign Now"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* VIEW SIGNED COPY MODAL */}
      {viewingSig && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 overflow-y-auto"
             onClick={() => setViewingSig(null)} data-testid="view-sig-modal">
          <div onClick={(e) => e.stopPropagation()} className="kr-card rounded-2xl p-6 sm:p-8 max-w-2xl w-full my-8 relative">
            <button onClick={() => setViewingSig(null)} className="absolute top-4 right-4 text-slate-400 hover:text-white"><X size={20} /></button>
            <div className="eyebrow mb-1">Signed Copy</div>
            <h2 className="font-display text-2xl uppercase mb-4">{viewingSig.document?.title || viewingSig.document_title}</h2>

            {(viewingSig.document?.body || viewingSig.document_body) && (
              <div className="bg-[#050A15] rounded-lg p-5 max-h-[40vh] overflow-y-auto border border-white/10 mb-5">
                <FormattedBody text={viewingSig.document?.body || viewingSig.document_body} />
              </div>
            )}

            <div className="border-t border-white/10 pt-4 space-y-2 text-sm">
              {viewingSig.co_signed_name && (
                <div className="flex justify-between"><span className="text-slate-400">Player name:</span><span className="text-white font-semibold">{viewingSig.signed_name}</span></div>
              )}
              <div className="flex justify-between">
                <span className="text-slate-400">{viewingSig.co_signed_name ? "Parent name:" : "Signed by:"}</span>
                <span className="text-white font-semibold">{viewingSig.co_signed_name || viewingSig.signed_name}</span>
              </div>
              <div className="flex justify-between"><span className="text-slate-400">Date signed:</span><span className="text-white">{fmtDate(viewingSig.signed_at)}</span></div>
              {viewingSig.photo_video_consent && (
                <div className="flex justify-between" data-testid="view-photo-video-consent">
                  <span className="text-slate-400">Photo/Video consent:</span>
                  <span className={viewingSig.photo_video_consent === "yes" ? "text-[#00E5FF] font-semibold" : "text-red-300 font-semibold"}>
                    {viewingSig.photo_video_consent === "yes" ? "Yes — Permission granted" : "No — Do not use"}
                  </span>
                </div>
              )}
              <div className="flex justify-between"><span className="text-slate-400">Confirmation:</span><span className="text-[#00E5FF]">Agreement accepted</span></div>
            </div>
          </div>
        </div>
      )}

      {/* MEDICAL FORM MODAL */}
      {medicalOpen && (
        <MedicalFormModal
          onClose={() => setMedicalOpen(false)}
          onSaved={load}
        />
      )}
    </div>
  );
}
